import { useParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { IHouseWithRentPeriod, IHouse } from "../../interfaces/interfaces";
import { usePromisesLoader } from "../../utils/usePromiseLoader";
import { HouseAPI } from "../../API/houseApi";
import { API } from "aws-amplify";
import { useRepublishHouse } from "../../API/queries/houseApiQueries";
import { useHospiNavigate } from "../../utils/common";
import { HOST_ROOMS_ROUTE } from "../../utils/constants";
import { useNotifier } from "../../context/ContextNotifier";
import {
  LoadingIndicator,
  LoadingIndicatorContainer
} from "../../components/LoadingIndicator";
import { getStepErrorsFromResponse } from "../../components/Wizard/utils";
import { scrollToFormWithError } from "../../utils/dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { SectionHeaderComponent } from "../ProfileUser/StudentProfileEdit";
import { WizardStepAvailabilityView } from "../../components/Wizard/House/Steps/WizardStepAvailability";
import { SectionContainer } from "../ProfileUser/StudentProfileEdit/styles";
import { Button } from "antd";
import { isEmpty } from "lodash";

const Root = styled.div`
  margin: 16px 16px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const InnerContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  padding: 80px 24px 20px;
  box-sizing: border-box;
`;
const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 16px;
`;

export const HouseRepublishPage = () => {
  const { id }: { id?: string } = useParams();
  const [house, setHouse] = useState<IHouseWithRentPeriod | null>(null);
  const [editedHouse, setEditedHouse] = useState<IHouseWithRentPeriod | null>(
    null
  );
  const [errors, setErrors] = useState<
    Partial<{
      [K in keyof IHouse]: string;
    }>
  >({});
  const { isLoading, watchPromise } = usePromisesLoader();
  const navigate = useHospiNavigate();
  const republish = useRepublishHouse();
  const notifier = useNotifier();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation("hostrooms");

  useEffect(() => {
    const promise = HouseAPI.getHouse(id);

    watchPromise(
      promise.then((h) => {
        setHouse(h);
        setEditedHouse(h);
      })
    );

    return () => {
      API.cancel(promise);
    };
  }, [id, watchPromise]);

  const handleSubmit = () => {
    if (!house?.house_id || !editedHouse) {
      return;
    }
    const payload = {
      available_from: editedHouse.available_from,
      available_to: editedHouse.available_to
    };

    republish.mutate(
      {
        id: house.house_id,
        body: payload
      },
      {
        onSuccess: () => {
          notifier.success({
            message: "House is republished"
          });
          navigate(HOST_ROOMS_ROUTE);
        },
        onError: (error: any) => {
          const errors = getStepErrorsFromResponse(error) ?? {};
          if (isEmpty(errors)) {
            notifier.error({
              message: error.response?.data?.message ?? error.toString()
            });
          } else {
            setErrors(getStepErrorsFromResponse(error) ?? {});
            scrollToFormWithError(rootRef.current!);
          }
        }
      }
    );
  };

  return (
    <Root ref={rootRef}>
      {editedHouse ? (
        <InnerContainer>
          <SectionContainer>
            <SectionHeaderComponent
              title={t("titleRepublish")}
              showEditButton={false}
            />
            <WizardStepAvailabilityView
              values={{
                available_from: editedHouse.available_from,
                available_to: editedHouse.available_to
              }}
              setValues={(values) => {
                setEditedHouse((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    available_from: values.available_from!,
                    available_to: values.available_to!
                  };
                });
              }}
              errors={errors}
              readonly={false}
              viewonly={false}
            />
          </SectionContainer>
          <ButtonsContainer>
            <Button
              onClick={() => {
                navigate(HOST_ROOMS_ROUTE);
              }}
              size="large"
            >
              {t("buttonCancel")}
            </Button>
            <Button onClick={handleSubmit} size="large" type="primary">
              {t("republishButtonText")}
            </Button>
          </ButtonsContainer>
        </InnerContainer>
      ) : null}
      {isLoading ? (
        <LoadingIndicatorContainer>
          <LoadingIndicator />
        </LoadingIndicatorContainer>
      ) : null}
    </Root>
  );
};
